/**
 * RedactionPreviewDialog — side-over publish workflow.
 *
 * Flow:
 *   1. Founder opens the dialog with a target skill.
 *   2. We fetch /learning/marketplace/skills/{id}/redaction-preview.
 *   3. Show original (left) vs masked (right) inside DraftDiffViewer.
 *   4. Founder reads, checks "I accept the redaction".
 *   5. Confirm → POST /learning/marketplace/publish with redactionHash.
 *
 * The dialog is intentionally non-modal (Sheet, side-over) — publishing is
 * a workflow, not an alert. Closing without confirming is safe.
 */
import type {
  MarketplaceSkill,
  PublishToMarketplaceResponse,
  RedactionPreview,
  SkillCatalogEntry,
  SkillDetail,
} from '@chaos/types/learning';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, Shield, ShieldOff } from 'lucide-react';
import * as React from 'react';

import { learningApi, learningKeys } from '../../lib/api/learning';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { useToaster } from '../ui/toast';
import { DraftDiffViewer } from './draft-diff-viewer';

type PublishableSkill = Pick<
  SkillCatalogEntry | SkillDetail | MarketplaceSkill,
  'skillId' | 'name' | 'version'
> & { key: string };

interface RedactionPreviewDialogProps {
  skill: PublishableSkill;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional callback fired after a successful publish. */
  onPublished?: (response: PublishToMarketplaceResponse) => void;
  /**
   * Initial target status. Defaults to "published" but a publisher can
   * intentionally pick a lower step (e.g. "candidate") if they want a
   * dry-run inspection.
   */
  defaultTargetStatus?: 'candidate' | 'exportable' | 'published';
}

const TARGET_LABEL: Record<'candidate' | 'exportable' | 'published', string> = {
  candidate: 'Candidate',
  exportable: 'Exportable',
  published: 'Published',
};

export function RedactionPreviewDialog({
  skill,
  open,
  onOpenChange,
  onPublished,
  defaultTargetStatus = 'published',
}: RedactionPreviewDialogProps) {
  const qc = useQueryClient();
  const { toast } = useToaster();
  const [accepted, setAccepted] = React.useState(false);
  const [target, setTarget] = React.useState<'candidate' | 'exportable' | 'published'>(
    defaultTargetStatus,
  );
  const [notes, setNotes] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setAccepted(false);
      setNotes('');
      setTarget(defaultTargetStatus);
    }
  }, [open, defaultTargetStatus]);

  const previewQ = useQuery({
    queryKey: learningKeys.redactionPreview(skill.skillId, 'anthropic_skill_md'),
    queryFn: () => learningApi.getRedactionPreview(skill.skillId, { format: 'anthropic_skill_md' }),
    enabled: open,
    staleTime: 60_000,
  });

  const publishM = useMutation({
    mutationFn: (input: { redactionHash: string }) =>
      learningApi.publishToMarketplace({
        skillId: skill.skillId,
        targetStatus: target,
        redactionAccepted: true,
        redactionHash: input.redactionHash,
        notes: notes || undefined,
      }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: learningKeys.marketplace });
      qc.invalidateQueries({ queryKey: learningKeys.skills });
      qc.invalidateQueries({ queryKey: learningKeys.skill(skill.skillId) });
      toast({
        title: 'Skill published',
        description: `${skill.name} now ${result.newStatus}.`,
      });
      onPublished?.(result);
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      toast({
        title: 'Publish failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  const preview: RedactionPreview | undefined = previewQ.data;
  const canConfirm = accepted && !!preview && !publishM.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[860px] sm:w-[860px] flex flex-col p-0">
        <SheetHeader className="border-b border-border bg-muted/30 px-4 py-3">
          <SheetTitle className="flex items-center gap-2 text-[14px] font-semibold">
            <Shield className="h-4 w-4" aria-hidden /> Publish to marketplace
          </SheetTitle>
          <SheetDescription className="flex items-center gap-2 text-[12px] text-muted-foreground">
            <span className="font-mono">{skill.key}</span>
            <span aria-hidden>·</span>
            <span>v{skill.version}</span>
            <span aria-hidden>·</span>
            <span>{skill.name}</span>
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4">
          {previewQ.isPending ? (
            <div className="flex items-center justify-center gap-2 py-12 text-[12px] text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Generating redaction preview…
            </div>
          ) : previewQ.isError ? (
            <div className="rounded border border-destructive/50 bg-destructive/5 p-3 text-[12px] text-destructive">
              <div className="mb-1 flex items-center gap-1 font-semibold">
                <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> Could not fetch preview
              </div>
              <div>{(previewQ.error as Error).message}</div>
            </div>
          ) : preview ? (
            <div className="flex flex-col gap-4">
              <RedactionSummaryStrip preview={preview} />
              <DraftDiffViewer
                before={preview.originalText}
                after={preview.redactedText}
                beforeLabel="Original (internal)"
                afterLabel="Masked (export)"
              />
              <TargetPicker target={target} onChange={setTarget} />
              <NotesField value={notes} onChange={setNotes} />
              <AcceptanceCheckbox accepted={accepted} onChange={setAccepted} />
            </div>
          ) : null}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border bg-muted/30 px-4 py-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={publishM.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canConfirm}
            onClick={() => {
              if (!preview) return;
              publishM.mutate({ redactionHash: preview.redactionHash });
            }}
          >
            {publishM.isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                Publishing…
              </>
            ) : (
              <>
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                Confirm publish as {TARGET_LABEL[target]}
              </>
            )}
          </Button>
        </footer>
      </SheetContent>
    </Sheet>
  );
}

function RedactionSummaryStrip({ preview }: { preview: RedactionPreview }) {
  const patterns = Object.entries(preview.replacementsByPattern);
  return (
    <div className="flex flex-col gap-2 rounded border border-border bg-muted/30 p-3 text-[12px]">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1 text-foreground">
          <ShieldOff className="h-3.5 w-3.5 text-muted-foreground" aria-hidden /> Original →
          <Shield className="h-3.5 w-3.5 text-success" aria-hidden /> Masked
        </span>
        <span className="text-muted-foreground">
          <span className="font-mono font-semibold tabular-nums text-foreground">
            {preview.totalReplacements}
          </span>{' '}
          replacement{preview.totalReplacements === 1 ? '' : 's'}
        </span>
      </div>
      {patterns.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {patterns.map(([pattern, count]) => (
            <span
              key={pattern}
              className="inline-flex items-center gap-1 rounded border border-border bg-background px-1.5 py-0.5 text-[11px] text-muted-foreground"
            >
              <span className="font-mono">{pattern}</span>
              <span className="font-mono tabular-nums text-foreground">{count}</span>
            </span>
          ))}
        </div>
      ) : (
        <span className="text-[11px] text-muted-foreground">No matches — nothing to redact.</span>
      )}
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>Format: {preview.format}</span>
        <span>
          Hash: <span className="font-mono">{preview.redactionHash.slice(0, 16)}…</span>
        </span>
      </div>
    </div>
  );
}

function TargetPicker({
  target,
  onChange,
}: {
  target: 'candidate' | 'exportable' | 'published';
  onChange: (next: 'candidate' | 'exportable' | 'published') => void;
}) {
  const options: Array<{
    value: 'candidate' | 'exportable' | 'published';
    label: string;
    hint: string;
  }> = [
    {
      value: 'candidate',
      label: 'Candidate',
      hint: 'Internal review — not yet listed in the marketplace.',
    },
    {
      value: 'exportable',
      label: 'Exportable',
      hint: 'Available for partner / sale exports.',
    },
    {
      value: 'published',
      label: 'Published',
      hint: 'Visible in the public marketplace; other workspaces can install.',
    },
  ];
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="text-[11px] uppercase tracking-wider text-muted-foreground">
        Target status
      </legend>
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
        {options.map((opt) => (
          <label
            key={opt.value}
            className={cn(
              'flex cursor-pointer flex-col gap-0.5 rounded border border-border bg-background p-2 text-[12px] transition-colors',
              target === opt.value ? 'border-foreground/30 bg-muted/40' : 'hover:bg-muted/40',
            )}
          >
            <div className="flex items-center gap-2">
              <input
                type="radio"
                checked={target === opt.value}
                onChange={() => onChange(opt.value)}
                aria-label={opt.label}
                className="cursor-pointer"
              />
              <span className="font-medium text-foreground">{opt.label}</span>
            </div>
            <span className="ml-5 text-[11px] text-muted-foreground">{opt.hint}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function NotesField({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  return (
    <Label className="flex flex-col gap-1.5 text-[12px]">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
        Notes (optional)
      </span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        placeholder="Why this transition? Audit log entry."
        className={cn(
          'rounded border border-border bg-background p-2 text-[12px] text-foreground',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
        )}
      />
    </Label>
  );
}

function AcceptanceCheckbox({
  accepted,
  onChange,
}: {
  accepted: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-2 rounded border border-border bg-warning/5 p-3 text-[12px]">
      <Checkbox
        checked={accepted}
        onCheckedChange={(next) => onChange(next === true)}
        id="redaction-accept"
        className="mt-0.5"
      />
      <Label htmlFor="redaction-accept" className="flex cursor-pointer flex-col gap-0.5">
        <span className="font-medium text-foreground">I accept the redaction as shown above.</span>
        <span className="text-[11px] text-muted-foreground">
          The masked artifact is what other workspaces will see. The original stays internal.
          Publishing records the SHA-256 hash of the masked text for audit.
        </span>
      </Label>
    </div>
  );
}
